import React, { useEffect, useState } from 'react'
// ...existing imports...
import Utils from '../../../../lib/Utils'
import { useServiceContext } from '~/root'
import ManageUserGroupsModal from '../manage-groups/Modal'
import { Button, FormGroup, Form } from 'react-bootstrap'
import { Formik, Field, yupToFormErrors } from 'formik'
import Modal from '~/components/Modal'
import Loader from '~/components/shared/Loader'
import { usersApiService } from '../../../../services/api/users'
import { useRcmTableData } from '~/hooks/useRcmTableData'
import CustomErrorMessage from '~/components/shared/ErrorMessage'
import { Select } from '~/components/Select'
import DatePicker from 'react-datepicker'
import ValidationSchema from './FormValidation'
import { useTranslation } from 'react-i18next'
import { toast } from '~/services/toast'
import { getUserId, updateUserInfo } from '~/services/auth'
import { useModal } from '~/contexts/ModalContext'

type UserDefaults = {
  id: number | null
  temporaryUser: boolean
  serviceAccount: boolean
  firstName: string
  lastName: string
  email: string
  department: string
  title: string
  accountName: string
  role: string
  expiresAt: Date | null
}

interface EditUserModalProps {
  userId?: number | null
  onClose?: () => void
}

const EditUserModal: React.FC<EditUserModalProps> = ({ userId = null, onClose }) => {
  const { t: translate } = useTranslation()
  const context = useServiceContext()
  const { constant } = context
  const tableData = useRcmTableData()
  const [isOpen, setIsOpen] = useState(false)
  const [user, setUser] = useState<any>({})
  const [loading, setLoading] = useState(false)
  const [roleOptions, setRoleOptions] = useState<Array<{ value: string; label: string }>>([])
  // Always use a fresh schema instance for Yup
  const modal = useModal()
  // Fix tomorrowDate definition
  const todayDate = new Date()
  const tomorrowDate = new Date(todayDate.getTime())
  tomorrowDate.setDate(todayDate.getDate() + 1)

  // Fix Yup branch error: provide all keys expected by schema in initial cast
  // For new users, let schema fill defaults
  const [formData, setFormData] = useState<UserDefaults>(Utils.getFormDefaults({}, ValidationSchema()) as UserDefaults)

  useEffect(() => {
    setIsOpen(true)
    setLoading(true)
    const fetchData = async () => {
      const rolesResponse = await usersApiService.getAvailableRoles()
      setRoleOptions(
        rolesResponse.map((r) => ({
          value: r.id,
          label: translate('ROLE_' + r.id)
        }))
      )

      if (userId) {
        const userData = await usersApiService.getById(userId)
        setUser(userData)
        const fallback: UserDefaults = {
          id: null,
          temporaryUser: false,
          serviceAccount: false,
          firstName: '',
          lastName: '',
          email: '',
          department: '',
          title: '',
          accountName: '',
          role: '',
          expiresAt: null
        }
        const safeUserData = typeof userData === 'object' && userData !== null ? userData : fallback
        const userDefaults: UserDefaults = {
          id: safeUserData.id ?? null,
          temporaryUser: !!safeUserData.expiresAt,
          serviceAccount: safeUserData.serviceAccount ?? false,
          firstName: safeUserData.firstName ?? '',
          lastName: safeUserData.lastName ?? '',
          email: safeUserData.email ?? '',
          department: safeUserData.department ?? '',
          title: safeUserData.title ?? '',
          accountName: safeUserData.accountName ?? '',
          role: safeUserData.role ?? '',
          expiresAt: safeUserData.expiresAt ? new Date(safeUserData.expiresAt as any) : null
        }
        setFormData(Utils.getFormDefaults(userDefaults, ValidationSchema()) as UserDefaults)
      } else {
        setFormData(Utils.getFormDefaults({}, ValidationSchema()) as UserDefaults)
      }
      setLoading(false)
    }
    fetchData()
  }, [userId])
  const doClose = () => {
    setIsOpen(false)
    if (typeof onClose === 'function') {
      onClose()
    }
  }

  return (
    <Modal show={isOpen} handleClose={doClose} title={translate(userId ? 'USERS_EDIT_H_TITLE' : 'USERS_ADD_H_TITLE')}>
      <Formik
        key={userId == null ? 'new' : String(userId)}
        initialValues={formData}
        enableReinitialize
        validate={(values: any) => {
          // Debug log for validation
          // Patch: allow empty accountName for create, match legacy JSX
          const schema = ValidationSchema()
          if (!userId) {
            schema.fields.accountName = schema.fields.accountName.notRequired()
          }
          return schema.validate(values, { abortEarly: false, context: values }).then(
            () => {
              return {}
            },
            (err: any) => {
              if (err) {
                console.log('Formik validation failed:', err)
              }
              return yupToFormErrors(err)
            }
          )
        }}
        onSubmit={(values: any) => {
          setLoading(true)
          const updatedUserObject: any = { ...values }
          if (values.temporaryUser && values.expiresAt) {
            const expiresAt = values.expiresAt instanceof Date ? values.expiresAt : new Date(values.expiresAt)
            expiresAt.setHours(0, 0, 0, 0)
            updatedUserObject.expiresAt = expiresAt.getTime()
          } else {
            updatedUserObject.expiresAt = null
          }

          let apiPromise: Promise<any>
          if (userId != null) {
            apiPromise = usersApiService.update(userId, updatedUserObject)
            apiPromise.then(
              () => {
                toast.success('USERS_UPDATE_SUCCESS_MSG')
                if (getUserId() === userId) {
                  updateUserInfo()
                }
                doClose()
                tableData.refreshTable()
              },
              (data: any) => {
                toast.error('USERS_UPDATE_FAIL_MSG' + ': ' + data.data.message)
                setLoading(false)
              }
            )
          } else {
            apiPromise = usersApiService.create(updatedUserObject)
            apiPromise.then(
              (data: any) => {
                toast.success('USERS_CREATE_SUCCESS_MSG')
                doClose()
                setTimeout(() => {
                  modal.showModal(<ManageUserGroupsModal userId={data.id} />)
                }, 200)
              },
              (data: any) => {
                toast.error('USERS_CREATE_FAIL_MSG' + ': ' + data.data.message)
                setLoading(false)
              }
            )
          }
        }}
      >
        {({ values, setFieldValue, handleSubmit }) => {
          return (
            <Loader blocking={loading}>
              <Modal.Body style={{ padding: 24 }}>
                {/* Sequence strictly matches screenshot: */}
                <FormGroup
                  controlId="f3e7fb9a-defc-481f-9f19-b9a7d9e425d0"
                  className="form-group"
                  style={{ width: '100%', textAlign: 'left' }}
                >
                  <label className="control-label" style={{ fontWeight: 500, textAlign: 'left', display: 'block' }}>
                    {translate('USERS_FORM_LABEL_FIRSTNAME')}
                    <span style={{ color: 'red' }}>* </span>
                  </label>
                  <Field
                    type="text"
                    name="firstName"
                    disabled={user.accountType === constant.user.accountType.SSO}
                    maxLength="255"
                    autoFocus
                    id="f3e7fb9a-defc-481f-9f19-b9a7d9e425d0"
                    className="form-control"
                  />
                  <CustomErrorMessage name="firstName" />
                </FormGroup>
                <FormGroup
                  controlId="d4e7fb9a-defc-481f-9f19-b9a7d9e425d1"
                  className="form-group"
                  style={{ width: '100%', textAlign: 'left' }}
                >
                  <label className="control-label" style={{ fontWeight: 500, textAlign: 'left', display: 'block' }}>
                    {translate('USERS_FORM_LABEL_LASTNAME')}
                    <span style={{ color: 'red' }}>* </span>
                  </label>
                  <Field
                    type="text"
                    name="lastName"
                    disabled={user.accountType === constant.user.accountType.SSO}
                    maxLength="255"
                    id="d4e7fb9a-defc-481f-9f19-b9a7d9e425d1"
                    className="form-control"
                  />
                  <CustomErrorMessage name="lastName" />
                </FormGroup>
                <FormGroup
                  controlId="e5e7fb9a-defc-481f-9f19-b9a7d9e425d2"
                  className="form-group"
                  style={{ width: '100%', textAlign: 'left' }}
                >
                  <label className="control-label" style={{ fontWeight: 500, textAlign: 'left', display: 'block' }}>
                    {translate('USERS_FORM_LABEL_EMAIL')}
                    <span style={{ color: 'red' }}>* </span>
                  </label>
                  <Field
                    type="email"
                    name="email"
                    disabled={user.accountType === constant.user.accountType.SSO}
                    maxLength="255"
                    id="e5e7fb9a-defc-481f-9f19-b9a7d9e425d2"
                    className="form-control"
                  />
                  <CustomErrorMessage name="email" />
                </FormGroup>
                <FormGroup
                  controlId="accountName-field"
                  className="form-group"
                  style={{ width: '100%', textAlign: 'left' }}
                >
                  <label className="control-label" style={{ fontWeight: 500, textAlign: 'left', display: 'block' }}>
                    {translate('USERS_FORM_LABEL_USERNAME')}
                    <span style={{ color: 'red' }}>* </span>
                  </label>
                  <Field
                    type="text"
                    name="accountName"
                    disabled={user.accountType === constant.user.accountType.SSO}
                    maxLength="255"
                    id="accountName-field"
                    className="form-control"
                  />
                  <CustomErrorMessage name="accountName" />
                </FormGroup>
                <FormGroup controlId="title-field" className="form-group" style={{ width: '100%', textAlign: 'left' }}>
                  <label className="control-label" style={{ fontWeight: 500, textAlign: 'left', display: 'block' }}>
                    {translate('TITLE_USER')}
                  </label>
                  <Field
                    type="text"
                    name="title"
                    disabled={user.accountType === constant.user.accountType.SSO}
                    maxLength="255"
                    id="title-field"
                    className="form-control"
                  />
                  <CustomErrorMessage name="title" />
                </FormGroup>
                <FormGroup
                  controlId="department-field"
                  className="form-group"
                  style={{ width: '100%', textAlign: 'left' }}
                >
                  <label className="control-label" style={{ fontWeight: 500, textAlign: 'left', display: 'block' }}>
                    {translate('DEPARTMENT')}
                  </label>
                  <Field
                    type="text"
                    name="department"
                    disabled={user.accountType === constant.user.accountType.SSO}
                    maxLength="255"
                    id="department-field"
                    className="form-control"
                  />
                  <CustomErrorMessage name="department" />
                </FormGroup>
                <FormGroup
                  controlId="4bc2f63d-be70-44aa-b4c9-c5eda512c0bc"
                  className="form-group"
                  style={{ width: '100%', textAlign: 'left' }}
                >
                  <label className="control-label" style={{ fontWeight: 500, textAlign: 'left', display: 'block' }}>
                    {translate('USERS_FORM_LABEL_ROLE')}
                    <span style={{ color: 'red' }}>*</span>
                  </label>
                  <Select
                    name="role"
                    value={values.role}
                    isDisabled={user.authType === constant.user.authType.CHECKMATE}
                    options={roleOptions}
                    isClearable={false}
                    className="field-control"
                    onChange={(option: any) => setFieldValue('role', option?.value ? option.value : null)}
                    id="4bc2f63d-be70-44aa-b4c9-c5eda512c0bc"
                  />
                  <CustomErrorMessage name="role" />
                </FormGroup>
                <FormGroup
                  controlId="36015f98-0457-42bb-afd4-c0437088d90a"
                  className="form-group"
                  style={{ width: '100%', textAlign: 'left' }}
                >
                  <label
                    className="control-label"
                    style={{ fontWeight: 500, textAlign: 'left', display: 'inline-flex', alignItems: 'center' }}
                  >
                    <Field
                      name="temporaryUser"
                      type="checkbox"
                      disabled={userId && user.authType !== constant.user.authType.RCM}
                      value={values.temporaryUser}
                      checked={values.temporaryUser}
                      as={Form.Check}
                      id="36015f98-0457-42bb-afd4-c0437088d90a"
                      style={{ marginRight: 8 }}
                    />
                    {translate('TEMPORARY_USER')}
                  </label>
                </FormGroup>
                {values.temporaryUser && (
                  <FormGroup
                    controlId="22f9a162-7b28-445e-ad3f-802e13a6c6de"
                    style={{ paddingLeft: 0, width: '100%', textAlign: 'left' }}
                  >
                    <Form.Label style={{ fontWeight: 500, textAlign: 'left', display: 'block' }}>
                      {translate('EXPIRATION_DATE')}
                    </Form.Label>
                    <DatePicker
                      name="expiresAt"
                      onChange={(val) => setFieldValue('expiresAt', val)}
                      selected={values.expiresAt}
                      popoverTargetOffset="3px 0"
                      todayButton="Today"
                      dateFormatCalendar="MMMM"
                      dateFormat="MMM dd, yyyy"
                      minDate={tomorrowDate}
                      showYearDropdown
                      className="form-control"
                      id="22f9a162-7b28-445e-ad3f-802e13a6c6de"
                    />
                    <CustomErrorMessage name="expiresAt" />
                  </FormGroup>
                )}
                {!user.ssomanaged && (
                  <FormGroup
                    controlId="4c29e96f-c821-41ca-8b2a-1f6313e91e9b"
                    className="form-group mt-5"
                    style={{ width: '100%', textAlign: 'left', marginTop: '2rem' }}
                  >
                    <label
                      className="control-label"
                      style={{ fontWeight: 500, textAlign: 'left', display: 'inline-flex', alignItems: 'center' }}
                    >
                      <Field
                        name="serviceAccount"
                        type="checkbox"
                        disabled={user.accountType === constant.user.accountType.SSO}
                        value={values.serviceAccount}
                        checked={values.serviceAccount}
                        as={Form.Check}
                        id="4c29e96f-c821-41ca-8b2a-1f6313e91e9b"
                        style={{ marginRight: 8 }}
                      />
                      {translate('USERS_FORM_LABEL_PSW_NEVER_EXPIRES')}
                    </label>
                  </FormGroup>
                )}
              </Modal.Body>
              <Modal.Footer>
                <Button
                  data-action={'cancel'}
                  variant="link"
                  onClick={doClose}
                  id="90df6066-f953-43c1-ad90-18e72ca9931b"
                >
                  {translate('CANCEL')}
                </Button>
                <Button
                  data-action={'save'}
                  className="btn btn-primary text-capitalize"
                  onClick={() => handleSubmit()}
                  id="3c8c2e21-00e8-4620-b459-a2513dcf500d"
                >
                  {translate('SAVE')}
                </Button>
              </Modal.Footer>
            </Loader>
          )
        }}
      </Formik>
    </Modal>
  )
}

export default EditUserModal
